import { IListingFilter } from '@models/IListingFilter';
import { IDidYouMeanResults, IListingResponse } from '@models/IListingResponse';
import { IListingResult } from '@models/IListingResult';
import { ISortOptions } from '@models/ISortOptions';
import { IStoredFilters } from '@models/IStoredFilters';
import ErrorBannerNotificationEnum from '@models/enums/ErrorBannerNotificationEnum';
import getAllowedListingFacets from '@utils/helpers/getAllowedListingFacets';
import removeFalsyValues from '@utils/helpers/removeFalsyValues';
import axios from 'axios';
import qs from 'qs';
import { useCallback, useMemo, useState } from 'react';
import useErrorNotificationStore from './ErrorBannerNotificationDisplayStore';

interface FetchResultsProps {
  readonly params: Record<string, unknown>;
  readonly allowedParams: string[];
  readonly from: number;
  readonly endpoint: string;
  readonly language: string;
  readonly pageSize: number;
  readonly page: number;
  readonly sortByParam: string;
  readonly showOnlyKits: boolean;
  readonly persistingParams: IStoredFilters;
  readonly dontSetResults?: boolean;
  readonly showAllVariants?: boolean;
  readonly listingId?: string;
}

interface ListingState {
  readonly results: IListingResult[];
  readonly filters: IListingFilter[];
  readonly loading: boolean;
  readonly error: boolean;
  readonly total: number;
  readonly hasNextPage: boolean;
  readonly sortOptions: Partial<ISortOptions>;
  readonly didYouMean?: IDidYouMeanResults;
}

const useListingStore = () => {
  const [listingState, setListingState] = useState<ListingState>({
    results: [],
    filters: [],
    error: false,
    loading: true,
    total: 0,
    hasNextPage: false,
    sortOptions: {},
    didYouMean: {},
  });

  const { setErrorBannerNotification } = useErrorNotificationStore();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  const fetchResults = useCallback(
    async ({
      params = {},
      allowedParams = [],
      endpoint = '',
      language = 'en',
      listingId,
      pageSize = 10,
      page = 0,
      sortByParam,
      showOnlyKits,
      persistingParams = {},
      dontSetResults,
      showAllVariants,
    }: FetchResultsProps) => {
      const whiteList = getAllowedListingFacets(params, allowedParams);
      const from = page * pageSize;
      const whiteListOfParams = {
        language,
        listingId,
        pageSize,
        from: from || 0,
        ...params,
        ...removeFalsyValues(persistingParams),
        ...(allowedParams.length > 0
          ? { ...whiteList, facetsToShow: allowedParams }
          : { ...params, facetsToShow: [] as string[] }),
        ...(sortByParam ? { sort: sortByParam } : {}),
        ...(showOnlyKits ? { showOnlyKits: true } : {}),
        ...(showAllVariants ? { showAllVariants: true } : {}),
      };

      try {
        setListingState((prev) => ({ ...prev, loading: true }));

        const { data } = await axios.get<IListingResponse | undefined>(
          endpoint,
          {
            params: whiteListOfParams,
            paramsSerializer: (parameters) =>
              qs.stringify(parameters, {
                arrayFormat: 'repeat',
                encode: false,
              }),
          }
        );

        if (!data) return;

        const getResults = (prev: ListingState) => {
          const dataItems = data.items ?? [];

          if (page > 0) return { results: [...prev.results, ...dataItems] };
          if (dontSetResults) return {};
          return { results: dataItems };
        };

        setListingState((prev) => ({
          ...prev,
          ...getResults(prev),
          filters: data.filters,
          total: data.total,
          hasNextPage: data.hasNextPage,
          sortOptions: data.sortOptions,
          didYouMean: data.didYouMean,
        }));
      } catch {
        setErrorBannerNotification(ErrorBannerNotificationEnum.SearchResults);

        setListingState((prev) => ({ ...prev, error: true }));
      } finally {
        setListingState((prev) => ({ ...prev, loading: false }));
      }
    },
    [setListingState]
  );

  const listingStore = useMemo(() => {
    return {
      ...listingState,
      fetchResults,
    };
  }, [fetchResults, listingState]);

  return listingStore;
};

export default useListingStore;
